import { type AppDetailsUrlMountQueryParams, ExtensionsUrls } from "@dashboard/extensions/urls";
import { type FlagList } from "@dashboard/featureFlags/availableFlags";
import { type ThemeType } from "@saleor/app-sdk/app-bridge";
import { useTheme } from "@saleor/macaw-ui";
import isEqualWith from "lodash/isEqualWith";
import { forwardRef, memo, useMemo, useRef } from "react";

interface AppIFrameProps {
  appId: string;
  src: string;
  featureFlags: FlagList;
  params?: AppDetailsUrlMountQueryParams;
  onLoad: () => void;
  onError?: () => void;
  className: string;
}

const _AppIFrame = forwardRef<HTMLIFrameElement, AppIFrameProps>(
  ({ appId, src, featureFlags, params, onLoad, onError, className }, ref) => {
    const { themeType } = useTheme();
    // Ignore updates to themeType - iframe will be notified via events
    // Otherwise this will cause reload of entire iframe
    const themeRef = useRef<ThemeType>(themeType);
    const paramsKey = JSON.stringify(params);
    const featureFlagsKey = JSON.stringify(featureFlags);

    /*
     * Javorina fork: build the iframe URL once per app location, not on every render.
     * A token refresh re-renders this component (new onLoad callback), and
     * resolveAppIframeUrl mixes the live window.location.search with `src`, whose path
     * comes from react-router's location - which app navigation (history.pushState)
     * never updates. Re-resolving here reloaded the iframe at the entry path with the
     * current query, dropping the user onto a broken page and losing their state.
     * The new token reaches the app via postMessage, so no reload is needed.
     */
    const iframeSrc = useMemo(
      () =>
        ExtensionsUrls.resolveAppIframeUrl(appId, src, {
          ...params,
          featureFlags,
          theme: themeRef.current,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [appId, src, paramsKey, featureFlagsKey],
    );

    return (
      <iframe
        data-test-id="app-frame"
        ref={ref}
        src={iframeSrc}
        onLoad={onLoad}
        onError={onError}
        className={className}
        sandbox="allow-same-origin allow-forms allow-scripts allow-downloads allow-popups"
        /*
         * Apps run cross origin, so the async Clipboard API is denied unless the policy is
         * delegated here - without it navigator.clipboard.writeText rejects and every app
         * with a copy button has to fall back to the deprecated document.execCommand.
         * Write only: apps have no reason to read what the user has on their clipboard.
         */
        allow="clipboard-write"
      />
    );
  },
);

_AppIFrame.displayName = "AppIFrame";

export const AppIFrame = memo(_AppIFrame, (prev, next) => isEqualWith(prev, next));
